import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { listAgents } from '../api/agents'
import { listIndustries } from '../api/industries'
import { AgentAvatar } from '../components/AgentAvatar'
import DemoChat from '../components/DemoChat'
import Footer from '../components/Footer'
import PricingSection from '../components/PricingSection'
import FAQSection from '../components/FAQSection'
import ComparisonTable from '../components/ComparisonTable'
import { useAuth } from '../hooks/useAuth'
import type { Agent, IndustryListItem } from '../types'

const AGENT_CAPABILITIES: Record<string, string[]> = {
  luna: [
    'Write SEO-optimized blog posts for your industry',
    'Create landing page copy that converts visitors',
    'Draft email newsletters and content calendars',
  ],
  eva: [
    'Audit your website for technical SEO issues',
    'Identify keyword gaps and ranking opportunities',
    'Recommend schema markup and meta tag improvements',
  ],
  max: [
    'Write cold emails that get replies from ideal clients',
    'Build multi-step follow-up sequences that close deals',
    'Craft LinkedIn outreach messages and scripts',
  ],
  noah: [
    'Write professional project proposals',
    'Create scopes of work with clear deliverables',
    'Draft pricing packages and contract language',
  ],
  tara: [
    'Build detailed project timelines and milestones',
    'Create client onboarding checklists',
    'Break complex projects into manageable tasks',
  ],
  grace: [
    'Draft professional emails to clients instantly',
    'Write project status updates and reports',
    'Handle difficult client conversations with diplomacy',
  ],
  aiden: [
    'Design automation workflows in Zapier and n8n',
    'Connect your apps to eliminate repetitive tasks',
    'Build lead nurturing and follow-up automations',
  ],
  quinn: [
    'Write platform-perfect Instagram, Facebook, and LinkedIn posts',
    'Create 30-day content calendars for your brand',
    'Generate captions, hashtags, and story ideas on demand',
  ],
}


const USE_CASES = [
  {
    agentId: 'luna',
    color: '#7C3AED',
    title: '"I don\'t have time to write content."',
    body: 'Luna generates SEO-optimized blog posts, email newsletters, and landing page copy in your brand voice — so you stop staring at a blank screen.',
    tag: 'Luna — SEO Writer',
  },
  {
    agentId: 'max',
    color: '#EF4444',
    title: '"I can\'t find new clients."',
    body: 'Max writes cold emails and multi-step follow-up sequences that actually get replies from the exact clients you want.',
    tag: 'Max — Outreach',
  },
  {
    agentId: 'noah',
    color: '#F59E0B',
    title: '"I keep losing proposals to bigger competitors."',
    body: 'Noah turns a quick briefing into a polished proposal with clear scope, deliverables, and pricing — in minutes, not days.',
    tag: 'Noah — Proposals',
  },
  {
    agentId: 'quinn',
    color: '#EC4899',
    title: '"My social media is dead."',
    body: 'Quinn creates platform-perfect posts for Instagram, Facebook, and LinkedIn every week — captions, hashtags, and all.',
    tag: 'Quinn — Social Media',
  },
  {
    agentId: 'eva',
    color: '#10B981',
    title: '"My website isn\'t showing up on Google."',
    body: 'Eva audits your site, finds the issues holding you back, and gives you a clear action plan to rank higher for searches that matter.',
    tag: 'Eva — SEO Auditor',
  },
  {
    agentId: 'aiden',
    color: '#6366F1',
    title: '"I waste hours on the same repetitive tasks."',
    body: 'Aiden designs Zapier and n8n automations to eliminate copy-paste work, missed follow-ups, and manual data entry.',
    tag: 'Aiden — Automation',
  },
  {
    agentId: 'grace',
    color: '#EC4899',
    title: '"Client emails eat up my entire day."',
    body: 'Grace drafts professional replies, project updates, and follow-ups in seconds — always on brand, always on time.',
    tag: 'Grace — Client Comms',
  },
  {
    agentId: 'tara',
    color: '#3B82F6',
    title: '"Projects fall through the cracks."',
    body: 'Tara creates detailed timelines, onboarding checklists, and task breakdowns so nothing slips and every client stays impressed.',
    tag: 'Tara — Project Manager',
  },
]


export default function Landing() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [agents, setAgents] = useState<Agent[]>([])
  const [industries, setIndustries] = useState<IndustryListItem[]>([])
  const [spotlightIdx, setSpotlightIdx] = useState(0)
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeIndustry, setActiveIndustry] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 20) }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setActiveIndustry(null)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    listAgents().then(setAgents).catch(() => {})
    listIndustries().then(setIndustries).catch(() => {})
  }, [])

  const startCycle = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (agents.length === 0) return
    intervalRef.current = setInterval(() => {
      setSpotlightIdx((i) => (i + 1) % agents.length)
    }, 4000)
  }, [agents.length])

  useEffect(() => {
    startCycle()
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [startCycle])

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  function handleSpotlightClick(idx: number) {
    setSpotlightIdx(idx)
    startCycle()
  }

  const spotlightAgent = agents[spotlightIdx] ?? null

  return (
    <div className="landing">
      <Helmet>
        <title>Aspired AI — AI Agents Built for Small Business</title>
        <meta name="description" content="Aspired AI gives small businesses a full AI team. 10 specialized agents for content, marketing, sales, and operations. Industry-trained. Cancel anytime. Start free." />
        <meta name="keywords" content="AI agents for small business, AI assistant for small business, AI marketing tool, AI team for business, artificial intelligence small business" />
        <meta property="og:title" content="Aspired AI — AI Agents Built for Small Business" />
        <meta property="og:description" content="Aspired AI gives small businesses a full AI team. 10 specialized agents for content, marketing, sales, and operations." />
        <meta property="og:url" content="https://aspired-ai.com" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://aspired-ai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://aspired-ai.com" />
      </Helmet>

      {/* ── HEADER ── */}
      <header>
        <nav role="navigation" aria-label="Main navigation" className={`landing-nav${scrolled ? ' scrolled' : ''}`}>
          <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}><img src="/owl-mascot.png" alt="Aspired AI" style={{ height: '24px', width: '24px', objectFit: 'contain', borderRadius: '4px', verticalAlign: 'middle', marginRight: '4px', display: 'inline-block' }} /> Aspired AI</div>
          <div className="nav-links">
            <button className="nav-link" onClick={() => scrollTo('how-it-works')}>How It Works</button>
            <button className="nav-link" onClick={() => scrollTo('agents')}>Agents</button>
            <button className="nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
            <button className="nav-link" onClick={() => navigate('/compare')}>Compare</button>
            <button className="nav-link" onClick={() => navigate('/faq')}>FAQ</button>
            <button className="nav-link" onClick={() => navigate('/blog')}>Blog</button>
            <button className="nav-link" onClick={() => scrollTo('demo')}>Try Demo</button>
          </div>
          <div className="nav-actions">
            {isAuthenticated ? (
              <button className="btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
            ) : (
              <>
                <button className="btn-ghost" onClick={() => navigate('/login')}>Sign in</button>
                <button className="btn-primary" onClick={() => navigate('/register')}>Start free</button>
              </>
            )}
          </div>
          <button className="nav-hamburger" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <line x1="2" y1="5" x2="20" y2="5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
              <line x1="2" y1="11" x2="20" y2="11" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
              <line x1="2" y1="17" x2="20" y2="17" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </nav>
      </header>

      {/* ── MOBILE MENU ── */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-top">
            <div className="nav-logo" onClick={() => { setMobileMenuOpen(false); navigate('/') }} style={{ cursor: 'pointer' }}><img src="/owl-mascot.png" alt="Aspired AI" style={{ height: '24px', width: '24px', objectFit: 'contain', borderRadius: '4px', verticalAlign: 'middle', marginRight: '4px', display: 'inline-block' }} /> Aspired AI</div>
            <button className="nav-hamburger" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <line x1="4" y1="4" x2="18" y2="18" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
                <line x1="18" y1="4" x2="4" y2="18" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <nav className="mobile-menu-nav">
            {[
              { label: 'How It Works', action: () => scrollTo('how-it-works') },
              { label: 'Agents', action: () => scrollTo('agents') },
              { label: 'Pricing', action: () => navigate('/pricing') },
              { label: 'Compare', action: () => navigate('/compare') },
              { label: 'FAQ', action: () => navigate('/faq') },
              { label: 'Blog', action: () => navigate('/blog') },
            ].map((item) => (
              <button
                key={item.label}
                className="mobile-menu-link"
                onClick={() => { setMobileMenuOpen(false); item.action() }}
              >
                {item.label}
              </button>
            ))}
          </nav>
          <div className="mobile-menu-actions">
            <button
              className="mobile-menu-signin"
              onClick={() => { setMobileMenuOpen(false); navigate('/login') }}
            >
              Sign In
            </button>
            <button
              className="btn-primary mobile-menu-start"
              onClick={() => { setMobileMenuOpen(false); navigate('/register') }}
            >
              Start Free
            </button>
          </div>
        </div>
      )}

      <main>

        {/* ── HERO ── */}
        <section id="hero" aria-label="Hero" className="hero-section">
          <div className="hero-grid-bg" aria-hidden="true" />
          <div className="hero-content">
            <div className="hero-badge">⚡ Built for Small Business Owners</div>
            <h1
              className="hero-headline"
              aria-label="Your AI Team. Built for Small Business."
            >
              Your AI Team.<br />
              <span className="hero-headline-gradient">Built for Small Business.</span>
            </h1>
            <p className="hero-subhead">
              10 specialized AI agents that know your industry — from restaurants to contractors
              to salons. Content, marketing, sales, and operations. Buy the agents you need.
            </p>
            <div className="hero-ctas">
              <Link to="/register" className="btn-primary btn-lg">
                Start Free — 15 Messages
              </Link>
              <button className="btn-outline btn-lg" onClick={() => scrollTo('how-it-works')}>
                See How It Works
              </button>
            </div>
            <p className="hero-demo-nudge">👇 Try it right now — no sign up needed</p>
          </div>
          <section id="demo" aria-label="Live demo" style={{ marginTop: '48px', width: '100%', maxWidth: '760px', margin: '48px auto 0' }}>
            <DemoChat />
          </section>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section id="how-it-works" aria-label="How Aspired AI works" className="hiw-section">
          <div className="hiw-inner">
            <div className="section-label-mono">HOW IT WORKS</div>
            <h2 className="section-h2">Your AI team in 3 steps</h2>
            <div className="hiw-steps">

              <div className="hiw-step">
                <div className="hiw-step-icon" style={{ background: 'rgba(124,58,237,0.12)', color: '#9D5CF6' }}>🏢</div>
                <h3 className="hiw-step-title">Pick Your Industry</h3>
                <p className="hiw-step-body">
                  Tell us what kind of business you run. Your agents instantly learn your
                  industry — the lingo, the challenges, and what your customers care about.
                </p>
              </div>

              <div className="hiw-connector" aria-hidden="true" />

              <div className="hiw-step">
                <div className="hiw-step-icon" style={{ background: 'rgba(124,58,237,0.12)', color: '#9D5CF6' }}>🤖</div>
                <h3 className="hiw-step-title">Choose Your Agents</h3>
                <p className="hiw-step-body">
                  Buy the specialists you need. A food truck owner might grab Luna for social
                  posts and Max for outreach. A law firm might start with Grace and Noah.
                </p>
              </div>

              <div className="hiw-connector" aria-hidden="true" />

              <div className="hiw-step">
                <div className="hiw-step-icon" style={{ background: 'rgba(124,58,237,0.12)', color: '#9D5CF6' }}>⚡</div>
                <h3 className="hiw-step-title">Get Things Done</h3>
                <p className="hiw-step-body">
                  Chat with your agents like a team member. They remember your business,
                  learn your style, and get smarter every conversation.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* ── MEET THE TEAM ── */}
        <section id="agents" aria-label="AI agent team" className="agents-section">
          <div className="agents-section-inner">
            <div className="section-label-mono">THE TEAM</div>
            <h2 className="section-h2">Meet your AI specialists</h2>
            <p className="section-sub">Each agent is an expert in their role — and they know your industry.</p>

            {/* Agent Spotlight */}
            {spotlightAgent && (
              <div
                className="agent-spotlight"
                style={{
                  '--agent-color': spotlightAgent.color,
                  borderLeftColor: spotlightAgent.color,
                  background: spotlightAgent.color + '0f',
                } as React.CSSProperties}
              >
                <div className="spotlight-left">
                  <AgentAvatar imageUrl={spotlightAgent.image_url} emoji={spotlightAgent.emoji} name={spotlightAgent.name} color={spotlightAgent.color} size={80} />
                  <div>
                    <div className="spotlight-name">{spotlightAgent.name}</div>
                    <div className="spotlight-role" style={{ color: spotlightAgent.color }}>
                      {spotlightAgent.role}
                    </div>
                    <span
                      className="spotlight-dept"
                      style={{ background: spotlightAgent.color + '22', color: spotlightAgent.color }}
                    >
                      {spotlightAgent.dept}
                    </span>
                  </div>
                </div>
                <div className="spotlight-right">
                  <div className="spotlight-label">What I can do for you:</div>
                  <ul className="spotlight-caps">
                    {(AGENT_CAPABILITIES[spotlightAgent.id] ?? []).map((cap) => (
                      <li key={cap} className="spotlight-cap">
                        <span className="spotlight-cap-arrow" style={{ color: spotlightAgent.color }}>→</span>
                        {cap}
                      </li>
                    ))}
                  </ul>
                  <button
                    className="spotlight-btn"
                    style={{ color: spotlightAgent.color }}
                    onClick={() => navigate('/register')}
                  >
                    Chat with {spotlightAgent.name} →
                  </button>
                </div>
              </div>
            )}

            {/* Agent Grid */}
            <div className="agents-grid">
              {agents.map((agent, idx) => (
                <div
                  key={agent.id}
                  className={`agent-showcase-card ${idx === spotlightIdx ? 'spotlight-active' : ''}`}
                  style={{ '--agent-color': agent.color } as React.CSSProperties}
                  onClick={() => handleSpotlightClick(idx)}
                  aria-label={`${agent.name} — ${agent.role} AI agent`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleSpotlightClick(idx)}
                >
                  <AgentAvatar imageUrl={agent.image_url} emoji={agent.emoji} name={agent.name} color={agent.color} size={48} />
                  <div className="agent-name">{agent.name}</div>
                  <div className="agent-role" style={{ color: agent.color }}>{agent.role}</div>
                  <span
                    className="agent-dept-badge"
                    style={{ background: agent.color + '20', color: agent.color }}
                  >
                    {agent.dept}
                  </span>
                  <p className="agent-desc">{agent.description}</p>
                  <div className="agent-card-bottom agent-card-bottom-col">
                    <span className="agent-price">${agent.monthly_price}/mo</span>
                    <button
                      className="agent-card-cta agent-card-cta-full"
                      style={{ color: agent.color }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/${agent.id}`) }}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── INDUSTRY MODAL ── */}
        {activeIndustry && (() => {
          const ind = industries.find((i) => i.slug === activeIndustry)
          if (!ind) return null
          return (
            <div className="industry-modal-overlay" onClick={() => setActiveIndustry(null)}>
              <div className="industry-modal-card" onClick={(e) => e.stopPropagation()}>
                <button className="industry-modal-close" onClick={() => setActiveIndustry(null)}>✕</button>
                <div className="industry-modal-top">
                  <span style={{ fontSize: 32 }}>{ind.emoji}</span>
                  <span className="industry-modal-name">{ind.name}</span>
                </div>
                <div className="industry-modal-label">AI FOR {ind.name.toUpperCase()}</div>
                <div className="industry-modal-title">{ind.tagline}</div>
                {ind.description && (
                  <p className="industry-modal-body">{ind.description}</p>
                )}
                <div className="industry-modal-footer">
                  <button
                    className="industry-modal-link"
                    onClick={() => { setActiveIndustry(null); navigate(`/industries/${ind.slug}`) }}
                  >
                    See the full page →
                  </button>
                  <button className="btn-primary" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => navigate('/register')}>
                    Start Free
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* ── INDUSTRIES ── */}
        <section id="industries" aria-label="Industries we support" className="industries-section">
          <div className="section-inner">
            <div className="section-label-mono">YOUR INDUSTRY</div>
            <h2 className="section-h2">AI agents that speak your language</h2>
            <p className="section-sub">Whether you run a food truck or a law firm, your agents know your world.</p>
            <div className="landing-industry-grid">
              {industries.map((ind) => (
                <button
                  key={ind.slug}
                  className={`landing-industry-tile${activeIndustry === ind.slug ? ' active' : ''}`}
                  onClick={() => setActiveIndustry(ind.slug)}
                  aria-label={`AI agents for ${ind.name}`}
                >
                  <span className="industry-tile-emoji" aria-hidden="true">{ind.emoji}</span>
                  <span className="industry-tile-label">{ind.name}</span>
                </button>
              ))}
            </div>
            <p className="industry-keyword-p">
              Aspired AI works for restaurants, food trucks, contractors, salons,
              real estate agents, retail stores, fitness studios, dental practices,
              law firms, marketing agencies, and e-commerce businesses. Every agent
              is trained with industry-specific knowledge so responses feel like they
              came from someone who truly understands your business.
            </p>
          </div>
        </section>

        {/* ── USE CASES ── */}
        <section id="use-cases" aria-label="What you can do with Aspired AI" className="use-cases-section">
          <div className="section-inner">
            <div className="section-label-mono">USE CASES</div>
            <h2 className="section-h2">What your AI team can do</h2>
            <div className="use-cases-grid">
              {USE_CASES.map((uc) => {
                const ucAgent = agents.find((a) => a.id === uc.agentId)
                return (
                  <div key={uc.title} className="use-case-card">
                    {ucAgent
                      ? <AgentAvatar imageUrl={ucAgent.image_url} emoji={ucAgent.emoji} name={ucAgent.name} color={ucAgent.color} size={48} />
                      : <div className="use-case-icon" aria-hidden="true" style={{ background: uc.color + '20', color: uc.color }}>🤖</div>
                    }
                    <h3 className="use-case-title">{uc.title}</h3>
                    <p className="use-case-body">{uc.body}</p>
                    <span className="use-case-tag" style={{ color: uc.color }}>{uc.tag}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── COMPARE TEASER ── */}
        <section id="compare" aria-label="How Aspired AI compares" className="compare-teaser-section">
          <div className="section-inner">
            <div className="section-label-mono">COMPARE</div>
            <h2 className="section-h2">How we stack up</h2>
            <p className="section-sub">Most AI platforms give you one chatbot or 12 generic helpers. We give you 58 specialists trained for your industry.</p>
            <ComparisonTable compact />
          </div>
        </section>

        {/* ── PRICING ── */}
        <section id="pricing" aria-label="Pricing plans" className="pricing-section">
          <div className="section-inner">
            <div className="section-label-mono">PRICING</div>
            <h2 className="section-h2">Buy the agents you need</h2>
            <p className="section-sub">Start with one. Add more as you grow. Bundle for a discount.</p>

            <PricingSection />
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" aria-label="Frequently asked questions" className="faq-section">
          <div className="section-inner faq-inner">
            <div className="section-label-mono">FAQ</div>
            <h2 className="section-h2">Common questions</h2>
            <FAQSection />
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section aria-label="Call to action" className="final-cta-section">
          <div className="final-cta-inner">
            <h2 className="final-cta-heading">Ready to hire your AI team?</h2>
            <p className="final-cta-sub">Start free. 15 messages. No credit card required.</p>
            <Link to="/register" className="btn-primary btn-lg">
              Get Started Free
            </Link>
            <p className="final-cta-proof">Join other small businesses already using Aspired AI</p>
          </div>
        </section>

      </main>

      <Footer />

    </div>
  )
}
